import { DocumentNode } from '@apollo/client';

import { NewlyCreated } from '@/modules/common/AddNewAnythingModal/AddNewAnythingModal';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { TestamentaryEntityKind } from '@/types/schema';

import { CreateTestamentaryEntityModal } from './CreateTestamentaryEntityModal';
import {
  EditTestamentaryEntitySection,
  EditTestamentaryEntitySplitScreenModal,
} from './EditTestamentaryEntitySplitScreenModal';
import {
  DefaultEstateTaxStatus,
  TestamentaryEntityTab,
} from './shared/testamentaryEntity.types';

/**
 * Maps the legacy TestamentaryEntityTab to the new EditTestamentaryEntitySection.
 */
function mapTabToSection(
  tab: TestamentaryEntityTab | undefined
): EditTestamentaryEntitySection {
  switch (tab) {
    case 'beneficiaries':
      return EditTestamentaryEntitySection.ACCESS_AFTER_DEATH;
    case 'beneficiaries-after-both-die':
      return EditTestamentaryEntitySection.ACCESS_AFTER_BOTH_DEATHS;
    case 'trustees':
      return EditTestamentaryEntitySection.TRUSTEES;
    default:
      return EditTestamentaryEntitySection.BASIC_INFORMATION;
  }
}

export interface TestamentaryEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  testamentaryEntityId?: string;
  dyingPrimaryClientId?: string | null;
  deadClientIds?: string[];
  isTwoClientHousehold: boolean;
  initialTab?: TestamentaryEntityTab;
  initialSection?: EditTestamentaryEntitySection;
  /** After a mutation, the modal will trigger a refetch of the active queries.
   * Pass any additional queries you want to ignore from the refetch here. */
  ignoredQueryDocuments?: DocumentNode[];
  defaultEstateTaxStatus?: DefaultEstateTaxStatus;
  defaultName?: string;
  defaultKind?: TestamentaryEntityKind;
  /**
   * Callback to run after creating a new testamentary entity that passes back
   * the new id and the display name
   */
  onAfterCreate?: (created: NewlyCreated) => void;
}

export function TestamentaryEntityModal(props: TestamentaryEntityModalProps) {
  const {
    isOpen,
    onClose,
    testamentaryEntityId,
    dyingPrimaryClientId = null,
    deadClientIds = [],
    isTwoClientHousehold,
    initialTab,
    initialSection,
    defaultEstateTaxStatus,
    defaultName,
    defaultKind,
    onAfterCreate,
    ignoredQueryDocuments,
  } = props;

  const { householdId } = useHouseholdDetailsContext();
  const isEditMode = !!testamentaryEntityId;

  if (!isOpen) {
    return null;
  }

  if (isEditMode) {
    return (
      <EditTestamentaryEntitySplitScreenModal
        isOpen={isOpen}
        testamentaryEntityId={testamentaryEntityId}
        isTwoClientHousehold={isTwoClientHousehold}
        initialSection={initialSection ?? mapTabToSection(initialTab)}
        initialFirstDeathClientId={dyingPrimaryClientId}
        onClose={onClose}
      />
    );
  }

  if (householdId) {
    return (
      <CreateTestamentaryEntityModal
        isOpen={isOpen}
        householdId={householdId}
        deadClientIds={deadClientIds}
        isTwoClientHousehold={isTwoClientHousehold}
        defaultEstateTaxStatus={defaultEstateTaxStatus}
        defaultName={defaultName}
        defaultKind={defaultKind}
        onAfterCreate={onAfterCreate}
        ignoredQueryDocuments={ignoredQueryDocuments}
        onClose={onClose}
      />
    );
  }

  return null;
}
